import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useCurrentUser } from "../hooks/useCurrentUser";
import type { OnboardingState, CurrentUserPlan, PlanFeatureKey } from "../hooks/useCurrentUser";

interface CurrentUserContextValue {
  role: string | null;
  allowedPages: string[] | null;
  allowedCampaigns: string[] | null;
  hasCampaigns: boolean;
  tenantSlug: string | null;
  tenantId: string | null;
  onboardingState: OnboardingState | null;
  plan: CurrentUserPlan | null;
  featureFlags: Partial<Record<PlanFeatureKey, boolean>>;
  loading: boolean;
  canAccessPage: (pageKey: string) => boolean;
  hasFeature: (key: PlanFeatureKey) => boolean;
  refetchUser: () => Promise<unknown> | void;
}

const EMPTY_CAMPAIGNS: string[] = [];
const EMPTY_FLAGS: Partial<Record<PlanFeatureKey, boolean>> = {};

const CurrentUserContext = createContext<CurrentUserContextValue>({
  role: null,
  allowedPages: null,
  allowedCampaigns: null,
  hasCampaigns: false,
  tenantSlug: null,
  tenantId: null,
  onboardingState: null,
  plan: null,
  featureFlags: EMPTY_FLAGS,
  loading: true,
  canAccessPage: () => false,
  hasFeature: () => false,
  refetchUser: () => {},
});

export const CurrentUserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data, loading, refetchUser } = useCurrentUser();
  const user = data?.user || null;
  const allowedPages = user?.allowedPages || null;
  const role = user?.role || null;
  const allowedCampaigns = role === 'viewer' ? EMPTY_CAMPAIGNS : (user?.allowedCampaigns || null);
  const tenantSlug = user?.tenantSlug || null;
  const tenantId = user?.tenantId || null;
  const onboardingState = user?.onboardingState || null;
  const plan = user?.plan || null;
  const featureFlags = user?.featureFlags || EMPTY_FLAGS;
  const hasCampaigns = allowedCampaigns === null || (allowedCampaigns !== null && allowedCampaigns.length > 0);

  const canAccessPage = useCallback((pageKey: string): boolean => {
    if (!allowedPages) return false;
    if (pageKey === "social-listening") return role === "admin";
    return allowedPages.includes("*") || allowedPages.includes(pageKey);
  }, [allowedPages, role]);

  const hasFeature = useCallback((key: PlanFeatureKey): boolean => {
    if (role === 'admin') return true;
    return featureFlags[key] === true;
  }, [featureFlags, role]);

  const value = useMemo<CurrentUserContextValue>(() => ({
    role,
    allowedPages,
    allowedCampaigns,
    hasCampaigns,
    tenantSlug,
    tenantId,
    onboardingState,
    plan,
    featureFlags,
    loading,
    canAccessPage,
    hasFeature,
    refetchUser,
  }), [
    role,
    allowedPages,
    allowedCampaigns,
    hasCampaigns,
    tenantSlug,
    tenantId,
    onboardingState,
    plan,
    featureFlags,
    loading,
    canAccessPage,
    hasFeature,
    refetchUser,
  ]);

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
};

export const usePageAccess = () => useContext(CurrentUserContext);

/** Convenience hook for premium-feature gating. */
export const usePlanFeature = (key: PlanFeatureKey): boolean => {
  const { hasFeature } = useContext(CurrentUserContext);
  return hasFeature(key);
};
